/**
 * Puerta de acceso de las pantallas con pestañas (006 · T017, research R-3).
 *
 * Extraída **sin cambios de comportamiento** de `HomeGate` (`routes/index.tsx`, 002 · T034) para
 * que Inicio, Asistencia, Perfil y el aviso de privacidad decidan lo mismo al abrirse, sea desde
 * la pestaña o tecleando la URL. En este orden:
 *
 * 1. Activación a medias (token en `sessionStorage`) → `/instalar` (iOS sin instalar) o `/registrar`.
 * 2. Sin clave de dispositivo → `/dispositivo-no-registrado` (sin clave no se firma ninguna
 *    petición, así que el puente de eventos del cliente no se enteraría).
 * 3. `GET /me` `401 UNAUTHENTICATED` → `/dispositivo-no-registrado?reason=session-expired`.
 * 4. Empresa `SUSPENDED` → `/servicio-suspendido`, **salvo** con `allowSuspended: true`
 *    (Asistencia, Perfil y el aviso son de solo lectura con la empresa suspendida, research R-12).
 * 5. `privacy_notice.pending` (o el flag del `428`) → `PRIVACY_PENDING`: la pantalla pinta la
 *    aceptación de la nueva versión.
 * 6. Todo bien → `READY` con `me`.
 *
 * Sin `me` todavía (006 · T065, US3-4 y R-20/R-24): sin red —la consulta queda en pausa o el
 * `fetch` no llega— → `OFFLINE`; cualquier otro error que el puente de `__root.tsx` no enruta (un
 * 5xx, una respuesta que no valida) → `ERROR`. Las dos traen `retry`. Sin esto, abrir la app sin
 * red la dejaba en "Cargando…" para siempre. La consulta de la clave es local (IndexedDB), así que
 * corre también sin red (`networkMode: 'always'`).
 *
 * `403 WORKER_INACTIVE` y `423 COMPANY_SUSPENDED` llegan como error y los enruta el puente de
 * `__root.tsx`; aquí no se duplica esa navegación.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { type StaffMe } from '@fermin/contracts';

import { ApiError, EVENT_BY_ERROR_CODE, NetworkError } from '../../api/client';
import { useOnlineStatus } from '../../api/online';
import { hasKey } from '../../device/keys';
import { isIos, isStandalone } from '../../device/platform';
import { useStaffMe } from '../access/api';
import { DEVICE_KEY_QUERY_KEY } from '../access/device-key-query';
import { usePrivacyRequired } from '../access/privacy-store';
import { getActivationToken } from '../activation/activation-store';

/** Motivo con el que la puerta manda a `/dispositivo-no-registrado` (la pantalla lo lee de la URL). */
export const SESSION_EXPIRED_REASON = 'session-expired';

export type AccessGateStatus = 'LOADING' | 'OFFLINE' | 'ERROR' | 'PRIVACY_PENDING' | 'READY';

export type AccessGateResult =
  | { status: 'LOADING'; me: StaffMe | undefined }
  | { status: 'OFFLINE'; me: undefined; retry: () => void }
  | { status: 'ERROR'; me: undefined; retry: () => void }
  | { status: 'PRIVACY_PENDING'; me: StaffMe }
  | { status: 'READY'; me: StaffMe };

export interface AccessGateOptions {
  /** `true` en Asistencia, Perfil y el aviso: con la empresa suspendida se sigue mostrando. */
  allowSuspended: boolean;
}

export function useAccessGate({ allowSuspended }: AccessGateOptions): AccessGateResult {
  const navigate = useNavigate();
  // Se lee una sola vez: si hay activación en curso no se consulta nada más.
  const [pendingActivation] = useState(() => getActivationToken());
  const privacyFlag = usePrivacyRequired();
  const online = useOnlineStatus();

  const deviceKey = useQuery<boolean>({
    queryKey: DEVICE_KEY_QUERY_KEY,
    queryFn: () => hasKey(),
    enabled: pendingActivation === null,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
    // Local (IndexedDB): sin red también se resuelve; con el `online` por defecto quedaba en pausa.
    networkMode: 'always',
  });
  const me = useStaffMe({ enabled: deviceKey.data === true });

  useEffect(() => {
    if (pendingActivation !== null) {
      void navigate({
        to: isIos() && !isStandalone() ? '/instalar' : '/registrar',
        replace: true,
      });
    }
  }, [pendingActivation, navigate]);

  const noDeviceKey = pendingActivation === null && deviceKey.data === false;
  useEffect(() => {
    if (noDeviceKey) {
      void navigate({ to: '/dispositivo-no-registrado', replace: true });
    }
  }, [noDeviceKey, navigate]);

  // `me.error` puede ser un `NetworkError` (sin `.is`) aunque el tipo diga `ApiError`.
  const meError: unknown = me.error;
  const sessionExpired = meError instanceof ApiError && meError.is('UNAUTHENTICATED');
  useEffect(() => {
    if (sessionExpired) {
      void navigate({
        to: '/dispositivo-no-registrado',
        search: { reason: SESSION_EXPIRED_REASON },
        replace: true,
      });
    }
  }, [sessionExpired, navigate]);

  const blockedBySuspension = !allowSuspended && me.data?.company.status === 'SUSPENDED';
  useEffect(() => {
    if (blockedBySuspension) {
      void navigate({ to: '/servicio-suspendido', replace: true });
    }
  }, [blockedBySuspension, navigate]);

  if (me.data === undefined && deviceKey.data === true && pendingActivation === null) {
    const retry = () => {
      void me.refetch();
    };
    if (meError instanceof NetworkError || (!online && !me.isError)) {
      return { status: 'OFFLINE', me: undefined, retry };
    }
    // Los códigos que enruta el puente de `__root.tsx` (y el 401 de arriba) no pintan nada: la
    // navegación ya está en camino. Un error que no es del catálogo (esquema inválido) es ERROR.
    const routedByBridge =
      meError instanceof ApiError && (sessionExpired || EVENT_BY_ERROR_CODE[meError.code] !== undefined);
    if (meError !== null && !routedByBridge) {
      return { status: 'ERROR', me: undefined, retry };
    }
  }
  if (me.data === undefined || blockedBySuspension) {
    return { status: 'LOADING', me: me.data };
  }
  if (me.data.privacy_notice.pending || privacyFlag) {
    return { status: 'PRIVACY_PENDING', me: me.data };
  }
  return { status: 'READY', me: me.data };
}
